import React, { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api, getApiBaseUrl } from '../../api/client';
import { useBranding } from '../../context/BrandingContext';
import { getSchemeColors } from '../../utils/themeColors';
import '../../App.css';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import MarkdownEditor from '../MarkdownEditor';
import { translateError } from '../../utils/errorMessages';
import { useSeedEffect, type SeedKey } from '../../hooks/useSeedEffect';

/**
 * Get the full URL for an image. If the URL is relative (starts with /),
 * prepend the API base URL. Mirrors EventForm's helper.
 */
const getImageUrl = (url: string): string => {
  if (url.startsWith('/')) {
    return `${getApiBaseUrl()}${url}`;
  }
  return url;
};

export interface ICoursePresentationData {
  coverImage: string;
  gallery: string[];
  extendedDescription: string;
  colorSettings: {
    primaryColor: string;
    secondaryColor: string;
  };
}

interface ICoursePresentationFormProps {
  initialData?: {
    coverImage?: string | null;
    gallery?: string[] | null;
    extendedDescription?: string | null;
    colorSettings?: { primaryColor?: string; secondaryColor?: string } | null;
  };
  /** Record identity — seeds once per record instead of per object identity. */
  seedKey?: SeedKey;
  onSubmit: (data: ICoursePresentationData) => void | Promise<void>;
  loading?: boolean;
}

const CoursePresentationForm: React.FC<ICoursePresentationFormProps> = ({
  initialData,
  seedKey,
  onSubmit,
  loading = false,
}) => {
  const { t } = useTranslation();
  const { colorScheme } = useBranding();
  const schemeDefaults = useMemo(() => getSchemeColors(colorScheme), [colorScheme]);

  const [coverImage, setCoverImage] = useState('');
  const [gallery, setGallery] = useState<string[]>([]);
  const [extendedDescription, setExtendedDescription] = useState('');
  const [primaryColor, setPrimaryColor] = useState(schemeDefaults.primary);
  const [secondaryColor, setSecondaryColor] = useState(schemeDefaults.light);

  const [coverUploading, setCoverUploading] = useState(false);
  const [coverError, setCoverError] = useState<string | null>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryError, setGalleryError] = useState<string | null>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // Seeded once per record (seedKey), else per initialData identity.
  useSeedEffect(initialData, seedKey, (seed) => {
    if (!seed) return;
    setCoverImage(seed.coverImage || '');
    setGallery(seed.gallery || []);
    setExtendedDescription(seed.extendedDescription || '');
    if (seed.colorSettings) {
      setPrimaryColor(seed.colorSettings.primaryColor || schemeDefaults.primary);
      setSecondaryColor(seed.colorSettings.secondaryColor || schemeDefaults.light);
    }
  });

  const validateImageFile = (file: File, setError: (msg: string | null) => void): boolean => {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('events.galleryInvalidType') || 'Only PNG, JPG, and WebP files are allowed');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(t('events.galleryFileTooLarge') || 'File size must be less than 5MB');
      return false;
    }
    setError(null);
    return true;
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateImageFile(file, setCoverError)) {
      if (coverInputRef.current) coverInputRef.current.value = '';
      return;
    }
    setCoverUploading(true);
    try {
      const { data } = await api.uploads.galleryImage(file);
      setCoverImage(data.url);
    } catch (err: any) {
      setCoverError(translateError(err, t, 'events.galleryUploadFailed') || 'Failed to upload image');
    } finally {
      setCoverUploading(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  const removeCover = async () => {
    if (coverImage.startsWith('/')) {
      try {
        await api.uploads.deleteGalleryImage(coverImage);
      } catch (err) {
        console.error('Failed to delete cover image from server:', err);
      }
    }
    setCoverImage('');
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateImageFile(file, setGalleryError)) {
      if (galleryInputRef.current) galleryInputRef.current.value = '';
      return;
    }
    setGalleryUploading(true);
    try {
      const { data } = await api.uploads.galleryImage(file);
      setGallery([...gallery, data.url]);
    } catch (err: any) {
      setGalleryError(translateError(err, t, 'events.galleryUploadFailed') || 'Failed to upload image');
    } finally {
      setGalleryUploading(false);
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const removeImage = async (index: number) => {
    const url = gallery[index];
    if (url.startsWith('/')) {
      try {
        await api.uploads.deleteGalleryImage(url);
      } catch (err) {
        console.error('Failed to delete image from server:', err);
      }
    }
    const newGallery = [...gallery];
    newGallery.splice(index, 1);
    setGallery(newGallery);
  };

  const submitHandler = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      coverImage,
      gallery,
      extendedDescription,
      colorSettings: {
        primaryColor,
        secondaryColor,
      },
    });
  };

  return (
    <form onSubmit={submitHandler} className="form-container">
      {/* Cover image */}
      <FormField htmlFor="course-cover-upload" label={t('courses.presentation.coverImage')}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            ref={coverInputRef}
            id="course-cover-upload"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
            onChange={handleCoverUpload}
            aria-label={t('courses.presentation.uploadImage')}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={coverUploading}
            onClick={() => coverInputRef.current?.click()}
          >
            {t('courses.presentation.uploadImage')}
          </Button>
        </div>
      </FormField>
      {coverError && (
        <div style={{ color: 'var(--error-color)', fontSize: '0.85rem', marginTop: '-0.5rem', marginBottom: '0.5rem' }}>
          {coverError}
        </div>
      )}
      {coverImage && (
        <div style={{ position: 'relative', display: 'inline-block', marginTop: '-0.5rem', marginBottom: '1rem' }}>
          <img
            src={getImageUrl(coverImage)}
            alt=""
            style={{
              maxWidth: '240px',
              maxHeight: '160px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              objectFit: 'cover',
              display: 'block',
            }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
          <Button type="button" variant="danger" size="sm" onClick={removeCover} style={{ marginTop: '0.5rem' }}>
            {t('courses.presentation.removeImage')}
          </Button>
        </div>
      )}

      {/* Gallery */}
      <FormField htmlFor="course-gallery-upload" label={t('courses.presentation.gallery')}>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
          <input
            ref={galleryInputRef}
            id="course-gallery-upload"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleGalleryUpload}
            disabled={galleryUploading}
            style={{ flex: 1 }}
            aria-label={t('events.uploadGalleryImage') || 'Upload gallery image'}
          />
          {galleryUploading && (
            <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              {t('common.uploading') || 'Uploading...'}
            </span>
          )}
        </div>
        {galleryError && (
          <div style={{ color: 'var(--error-color)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            {galleryError}
          </div>
        )}
        <small style={{ color: 'var(--text-muted)', display: 'block', marginBottom: '0.5rem' }}>
          {t('events.galleryHint') || 'PNG, JPG, or WebP. Max 5MB per image.'}
        </small>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.5rem' }}>
          {gallery.map((url, index) => (
            <div key={index} style={{ position: 'relative', border: '1px solid var(--border-color)', borderRadius: '4px', overflow: 'hidden', aspectRatio: '1' }}>
              <img
                src={getImageUrl(url)}
                alt={`Gallery ${index + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              <button
                type="button"
                onClick={() => removeImage(index)}
                style={{ position: 'absolute', top: '2px', right: '2px', background: 'rgba(255,0,0,0.7)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}
                aria-label={t('events.removeImage') || 'Remove image'}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </FormField>

      {/* Extended description */}
      <FormField
        htmlFor="course-extended-description"
        label={t('courses.presentation.description')}
        labelHelpText={t('courses.presentation.descriptionHelp')}
      >
        <MarkdownEditor
          id="course-extended-description"
          placeholder={t('courses.presentation.descriptionHelp')}
          value={extendedDescription}
          onChange={setExtendedDescription}
          rows={8}
        />
      </FormField>

      {/* Color settings */}
      <div className="form-group">
        <label>{t('events.colorSettings') || 'Color Theme'}</label>
        <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.8rem', margin: 0 }}>{t('courses.presentation.primaryColor')}</label>
            <input
              type="color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              style={{ width: '40px', height: '30px', padding: 0, border: 'none' }}
              aria-label={t('courses.presentation.primaryColor')}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.8rem', margin: 0 }}>{t('courses.presentation.secondaryColor')}</label>
            <input
              type="color"
              value={secondaryColor}
              onChange={(e) => setSecondaryColor(e.target.value)}
              style={{ width: '40px', height: '30px', padding: 0, border: 'none' }}
              aria-label={t('courses.presentation.secondaryColor')}
            />
          </div>
        </div>
      </div>

      <Button type="submit" fullWidth loading={loading}>
        {t('common.save')}
      </Button>
    </form>
  );
};

export default CoursePresentationForm;
